import React, { useMemo, useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { SECTIONS } from "../lib/checklistSchema";
import { FieldRenderer } from "../components/FieldRenderer";
import { BrandMark } from "../components/BrandMark";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { api } from "../lib/api";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Clock,
  ShieldCheck,
  Home as HomeIcon,
} from "lucide-react";

const HERO_IMG = "/brand/hero-home.jpg";
const AGENT_IMG = "/brand/louis-fong.jpg";

const validateSection = (section, data) => {
  const errors = {};
  section.fields.forEach((f) => {
    if (!f.required) return;
    const v = data[f.key];
    if (f.type === "consent") {
      if (!v) errors[f.key] = "Required to submit.";
    } else if (v === undefined || v === null || v === "") {
      errors[f.key] = "This field is required.";
    }
  });
  return errors;
};

export default function ChecklistPage() {
  const [started, setStarted] = useState(false);
  const [stepIdx, setStepIdx] = useState(0);
  const [data, setData] = useState({});
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const formTop = useRef(null);

  const currentSection = SECTIONS[stepIdx];
  const isLast = stepIdx === SECTIONS.length - 1;
  const isFirst = stepIdx === 0;
  const progress = ((stepIdx + 1) / SECTIONS.length) * 100;

  useEffect(() => {
    if (started && formTop.current) {
      formTop.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [stepIdx, started]);

  const setField = (key, value) => {
    setData((d) => ({ ...d, [key]: value }));
    setErrors((e) => {
      if (!e[key]) return e;
      const n = { ...e };
      delete n[key];
      return n;
    });
  };

  const handleNext = () => {
    const errs = validateSection(currentSection, data);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete the required fields.");
      return;
    }
    setStepIdx((i) => Math.min(i + 1, SECTIONS.length - 1));
  };

  const handleBack = () => setStepIdx((i) => Math.max(0, i - 1));

  const handleSubmit = async () => {
    const errs = validateSection(currentSection, data);
    if (Object.keys(errs).length) {
      setErrors(errs);
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/submissions", { data });
      navigate("/thank-you");
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!started) {
    return (
      <div className="min-h-screen bg-[#FAFAF8]" data-testid="landing-page">
        {/* Nav */}
        <header className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 flex items-center justify-between">
          <BrandMark />
          <a
            href="/admin/login"
            className="text-[12px] uppercase tracking-[0.2em] text-[#737373] hover:text-[#010082] transition-colors"
            data-testid="admin-nav-link"
          >
            Admin
          </a>
        </header>

        {/* Hero */}
        <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-16 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-[#E5E5E0] text-[11px] uppercase tracking-[0.2em] text-[#010082]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#010082]" />
              Seller Intake Checklist
            </div>
            <h1
              className="font-serif-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.05] tracking-tight text-[#1A1A1A] mt-6"
              data-testid="hero-headline"
            >
              Every detail we need to
              <span className="text-[#010082]"> sell your home</span> with confidence.
            </h1>
            <p className="text-[16px] leading-relaxed text-[#4A4A4A] mt-6 max-w-xl">
              This is Louis Fong's guided checklist for Equitas Homes sellers. It captures
              everything from timeline and features to access and disclosures — so we can
              price it right, market it beautifully, and close it smoothly.
            </p>

            <div className="flex flex-wrap gap-6 mt-8 text-[13px] text-[#4A4A4A]">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-[#010082]" strokeWidth={1.5} />
                About 10 minutes
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-[#010082]" strokeWidth={1.5} />
                Private &amp; confidential
              </div>
              <div className="flex items-center gap-2">
                <HomeIcon className="w-4 h-4 text-[#010082]" strokeWidth={1.5} />
                9 short sections
              </div>
            </div>

            <div className="mt-10 flex flex-wrap gap-4">
              <Button
                onClick={() => setStarted(true)}
                className="h-12 px-8 bg-[#010082] hover:bg-[#010082]/90 text-white rounded-lg text-[14px] tracking-wide"
                data-testid="start-checklist-button"
              >
                Begin Checklist
                <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
              </Button>
              <Button
                variant="outline"
                onClick={() => {
                  document
                    .getElementById("what-inside")
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                className="h-12 px-8 border-[#010082] text-[#010082] hover:bg-[#010082]/[0.04] rounded-lg text-[14px] bg-transparent"
                data-testid="learn-more-button"
              >
                What's inside
              </Button>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="relative rounded-2xl overflow-hidden border border-[#E5E5E0] shadow-sm bg-white">
              <img
                src={HERO_IMG}
                alt="Elegant living room"
                className="w-full h-[420px] object-cover"
              />
            </div>
            <div className="absolute -bottom-8 -left-8 w-56 rounded-2xl overflow-hidden border border-[#E5E5E0] bg-white shadow-md p-4 hidden md:block">
              <div className="flex items-center gap-3">
                <img
                  src={AGENT_IMG}
                  alt="Louis Fong"
                  className="w-16 h-16 rounded-full object-cover object-top border border-[#E5E5E0]"
                />
                <div>
                  <div className="text-[13px] font-medium text-[#1A1A1A]">Louis Fong</div>
                  <div className="text-[11px] text-[#737373]">Realtor · Equitas Homes</div>
                </div>
              </div>
              <p className="text-[12px] text-[#4A4A4A] mt-3 leading-relaxed">
                "Sharing the full picture up front is the single biggest lift for your sale."
              </p>
            </div>
          </div>
        </section>

        {/* What's inside */}
        <section
          id="what-inside"
          className="max-w-6xl mx-auto px-6 lg:px-10 pb-24 pt-6"
          data-testid="whats-inside-section"
        >
          <div className="grid md:grid-cols-3 gap-4 md:gap-6">
            {SECTIONS.map((s, i) => (
              <div
                key={s.id}
                className="bg-white border border-[#E5E5E0] rounded-xl p-6 hover:border-[#010082]/40 transition-colors"
                data-testid={`section-preview-${s.id}`}
              >
                <div className="text-[11px] uppercase tracking-[0.2em] text-[#010082]">
                  Section {String(i + 1).padStart(2, "0")}
                </div>
                <div className="font-serif-display text-xl mt-2 text-[#1A1A1A]">
                  {s.title}
                </div>
                <div className="text-[13px] text-[#737373] mt-2 leading-relaxed">
                  {s.subtitle}
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 flex justify-center">
            <Button
              onClick={() => setStarted(true)}
              className="h-12 px-8 bg-[#010082] hover:bg-[#010082]/90 text-white rounded-lg text-[14px]"
              data-testid="start-checklist-button-bottom"
            >
              Start the Checklist
              <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
            </Button>
          </div>
        </section>

        <footer className="border-t border-[#E5E5E0] py-10">
          <div className="max-w-6xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4 text-[12px] text-[#737373]">
            <BrandMark size={32} />
            <div data-testid="footer-copy">
              © {new Date().getFullYear()} Equitas Homes · Louis Fong, Realtor
            </div>
          </div>
        </footer>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAF8]" ref={formTop} data-testid="checklist-form-page">
      <header className="border-b border-[#E5E5E0] bg-white/70 backdrop-blur sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <BrandMark size={32} />
          <div className="text-[12px] text-[#737373]" data-testid="progress-text">
            Section {stepIdx + 1} of {SECTIONS.length}
          </div>
        </div>
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-4">
          <Progress
            value={progress}
            className="h-1 bg-[#E5E5E0] [&>div]:bg-[#010082]"
            data-testid="progress-bar"
          />
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 lg:px-10 py-10 lg:py-16 grid lg:grid-cols-12 gap-10">
        {/* Sidebar */}
        <aside className="lg:col-span-4">
          <div className="sticky top-32 space-y-1" data-testid="section-sidebar">
            {SECTIONS.map((s, i) => {
              const isActive = i === stepIdx;
              const isDone = i < stepIdx;
              return (
                <button
                  key={s.id}
                  onClick={() => (isDone ? setStepIdx(i) : null)}
                  className={`w-full text-left flex items-start gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? "bg-[#010082] text-white"
                      : isDone
                        ? "bg-white border border-[#E5E5E0] hover:border-[#010082]/40 text-[#1A1A1A]"
                        : "text-[#737373]"
                  }`}
                  data-testid={`sidebar-step-${s.id}`}
                >
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center text-[11px] flex-shrink-0 ${
                      isActive
                        ? "bg-white text-[#010082]"
                        : isDone
                          ? "bg-[#010082] text-white"
                          : "bg-[#E5E5E0] text-[#737373]"
                    }`}
                  >
                    {isDone ? <Check className="w-3.5 h-3.5" /> : i + 1}
                  </div>
                  <div className="flex-1">
                    <div
                      className={`text-[11px] uppercase tracking-[0.18em] ${
                        isActive ? "text-white/70" : "text-[#a3a3a3]"
                      }`}
                    >
                      Section {String(i + 1).padStart(2, "0")}
                    </div>
                    <div
                      className={`text-[14px] font-medium mt-0.5 ${
                        isActive ? "text-white" : ""
                      }`}
                    >
                      {s.title}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </aside>

        {/* Form */}
        <section className="lg:col-span-8">
          <div
            key={currentSection.id}
            className="section-enter bg-white border border-[#E5E5E0] rounded-2xl p-8 lg:p-10 shadow-sm"
            data-testid={`section-${currentSection.id}`}
          >
            <div className="text-[11px] uppercase tracking-[0.22em] text-[#010082]">
              Section {String(stepIdx + 1).padStart(2, "0")}
            </div>
            <h2 className="font-serif-display text-3xl sm:text-4xl text-[#1A1A1A] mt-2 tracking-tight">
              {currentSection.title}
            </h2>
            <p className="text-[14px] text-[#737373] mt-3 max-w-lg">
              {currentSection.subtitle}
            </p>

            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-6">
              {currentSection.fields.map((field) => {
                const wide =
                  ["textarea", "checkboxes", "radio", "consent"].includes(field.type) ||
                  ["seller_name", "property_address"].includes(field.key);
                return (
                  <div
                    key={field.key}
                    className={wide ? "md:col-span-2" : ""}
                    data-testid={`field-wrapper-${field.key.replace(/_/g, "-")}`}
                  >
                    <FieldRenderer
                      field={field}
                      value={data[field.key]}
                      onChange={(v) => setField(field.key, v)}
                      error={errors[field.key]}
                    />
                  </div>
                );
              })}
            </div>

            <div className="mt-12 pt-6 border-t border-[#E5E5E0] flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4">
              <Button
                variant="ghost"
                onClick={handleBack}
                disabled={isFirst}
                className="h-11 px-5 text-[#010082] hover:bg-[#010082]/[0.04] disabled:opacity-40 rounded-lg"
                data-testid="back-button"
              >
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.75} />
                Back
              </Button>

              {isLast ? (
                <Button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="h-12 px-8 bg-[#010082] hover:bg-[#010082]/90 text-white rounded-lg text-[14px]"
                  data-testid="submit-checklist-button"
                >
                  {submitting ? "Submitting…" : "Submit Checklist"}
                  <Check className="w-4 h-4 ml-2" strokeWidth={1.75} />
                </Button>
              ) : (
                <Button
                  onClick={handleNext}
                  className="h-12 px-8 bg-[#010082] hover:bg-[#010082]/90 text-white rounded-lg text-[14px]"
                  data-testid="next-button"
                >
                  Continue
                  <ArrowRight className="w-4 h-4 ml-2" strokeWidth={1.75} />
                </Button>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
